import { useState, useEffect } from 'react';
import { HelpButton } from '../../components/HelpButton';
import { useParams, useNavigate } from 'react-router';
import { myPartnersService } from '../../lib/api-service';
import type { PartnerDetail } from '../../lib/types';
import { CheckCircle, AlertTriangle } from 'lucide-react';

export default function PartnerDpa() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [partner, setPartner] = useState<PartnerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!id) return;
    myPartnersService.list().then((res) => {
      const p = res.results.find((r) => r.id === Number(id));
      setPartner(p || null);
      setLoading(false);
    });
  }, [id]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--vivid-purple)] border-t-transparent" />
      </div>
    );
  }

  if (!partner) {
    return <div className="p-8 text-center text-muted-foreground">Prietenul nu a fost găsit.</div>;
  }

  const isNatural = partner.entity_type === 'natural';
  const missingFields: string[] = [];
  if (!partner.legal_name) missingFields.push(isNatural ? 'Numele complet' : 'Denumirea legală');
  if (!partner.cif) missingFields.push(isNatural ? 'CNP' : 'CIF/CUI');
  if (!partner.legal_address) missingFields.push(isNatural ? 'Adresa' : 'Adresa sediului social');
  if (!isNatural) {
    if (!partner.legal_representative_name) missingFields.push('Numele reprezentantului legal');
    if (!partner.legal_representative_role) missingFields.push('Funcția reprezentantului legal');
  }

  async function handleAcceptDpa() {
    if (!partner) return;
    setAccepting(true);
    setError('');
    try {
      await myPartnersService.acceptDpa(partner.id);
      setSuccess(true);
    } catch (err) {
      if (err instanceof Error && err.message.includes('400')) {
        setError('Completează toate datele legale înainte de a accepta DPA.');
      } else {
        setError('A apărut o eroare. Încearcă din nou.');
      }
    } finally {
      setAccepting(false);
    }
  }

  if (success || partner.dpa_accepted_at) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8 text-center">
        <CheckCircle className="mx-auto mb-4 h-16 w-16 text-green-500" />
        <h1 className="mb-2 text-2xl font-bold" style={{ color: 'var(--vivid-purple)', fontFamily: 'var(--font-primary)' }}>
          DPA Acceptat
        </h1>
        <p className="text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
          Acordul de prelucrare a datelor a fost acceptat{partner.dpa_accepted_at ? ` la ${new Date(partner.dpa_accepted_at).toLocaleDateString('ro-RO')}` : ''}.
          {isNatural ? ' Persoana ' : ' Organizația '}<strong>{partner.legal_name || partner.name}</strong> este validată.
        </p>
        <button
          onClick={() => navigate('/staff/organizatiile-mele')}
          className="mt-6 rounded-lg bg-[var(--vivid-purple)] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90"
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          Înapoi la organizații
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8" style={{ fontFamily: 'var(--font-secondary)' }}>
      <div className="mb-2 flex items-center gap-2">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--vivid-purple)', fontFamily: 'var(--font-primary)' }}>
          Acord de Prelucrare a Datelor (DPA)
        </h1>
        <HelpButton to="/staff/documentatie/dpa" />
      </div>
      <p className="mb-6 text-sm text-muted-foreground">
        Pentru a accesa listele de participanți, organizația ta trebuie să accepte acest acord.
      </p>

      {missingFields.length > 0 && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-800">Câmpuri lipsă</p>
              <p className="text-sm text-amber-700">
                Completează următoarele câmpuri în profilul organizației înainte de a accepta DPA:
              </p>
              <ul className="mt-1 list-inside list-disc text-sm text-amber-700">
                {missingFields.map((f) => <li key={f}>{f}</li>)}
              </ul>
              <button
                onClick={() => navigate('/staff/organizatiile-mele')}
                className="mt-2 text-sm font-semibold text-amber-800 underline"
              >
                Editează profilul organizației
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="prose prose-sm max-w-none rounded-xl border bg-white p-6 shadow-sm">
        <h2>ACORD DE PRELUCRARE A DATELOR CU CARACTER PERSONAL</h2>
        <p><em>Conform Regulamentului (UE) 2016/679 (GDPR)</em></p>

        <h3>1. Părțile</h3>
        <p>
          <strong>Operatorul de date:</strong><br />
          Asociația Eu Sunt, CIF 34347234<br />
          Sediu: Calea Moților, nr. 24, Alba Iulia, jud. Alba<br />
          Email: contact.ctro@albaiulia2026.ro
        </p>
        {isNatural ? (
          <p>
            <strong>Procesatorul de date (persoană fizică):</strong><br />
            {partner.legal_name || '[Nume complet]'}, CNP {partner.cif || '[CNP]'}<br />
            Adresă: {partner.legal_address || '[Adresă]'}<br />
            Persoana acționează în nume propriu și răspunde personal pentru obligațiile
            asumate prin prezentul acord.
          </p>
        ) : (
          <p>
            <strong>Procesatorul de date (persoană juridică):</strong><br />
            {partner.legal_name || '[Denumire legală]'}, CIF {partner.cif || '[CIF]'}<br />
            Sediu: {partner.legal_address || '[Adresă]'}<br />
            Reprezentant legal: {partner.legal_representative_name || '[Nume]'}, {partner.legal_representative_role || '[Funcție]'}
          </p>
        )}

        <h3>2. Obiectul acordului</h3>
        <p>
          Prezentul acord reglementează prelucrarea datelor cu caracter personal ale participanților
          la activitățile organizate de Procesator prin intermediul platformei „Alba Iulia — Capitala
          Tineretului din România 2026".
        </p>

        <h3>3. Datele prelucrate</h3>
        <p>Procesatorul va avea acces la următoarele date ale participanților:</p>
        <ul>
          <li>Nume și prenume</li>
          <li>Adresă de email</li>
          <li>Număr de telefon</li>
          <li>Vârsta (calculată din data nașterii)</li>
        </ul>

        <h3>4. Scopul prelucrării</h3>
        <p>
          Datele sunt puse la dispoziția Procesatorului exclusiv în scopul organizării și desfășurării
          activităților la care participanții s-au înscris, inclusiv: comunicarea cu participanții,
          gestionarea prezenței, și asigurarea siguranței (verificare vârstă).
        </p>

        <h3>5. Durata</h3>
        <p>
          Procesatorul are acces la datele participanților pe durata colaborării cu Operatorul,
          dar nu mai mult de 30 de zile de la data încheierii fiecărei activități.
        </p>

        <h3>6. Obligațiile Procesatorului</h3>
        <p>Procesatorul se obligă:</p>
        <ol>
          <li>Să prelucreze datele exclusiv conform instrucțiunilor Operatorului și în scopurile stabilite.</li>
          <li>Să asigure confidențialitatea datelor și să instruiască personalul în acest sens.</li>
          <li>Să implementeze măsuri tehnice și organizatorice adecvate pentru protecția datelor.</li>
          <li>Să nu angajeze sub-procesatori fără acordul prealabil scris al Operatorului.</li>
          <li>Să asiste Operatorul în răspunsul la cererile persoanelor vizate (acces, rectificare, ștergere).</li>
          <li>Să notifice Operatorul în maximum 48 de ore de la descoperirea oricărei breșe de securitate.</li>
          <li>Să nu transfere datele în afara Spațiului Economic European fără acord prealabil.</li>
          <li>Să șteargă sau să returneze toate datele personale la încetarea acordului.</li>
          <li>Să nu utilizeze datele în scopuri proprii de marketing sau în alte scopuri decât cele stabilite.</li>
        </ol>

        <h3>7. Drepturile Operatorului</h3>
        <p>Operatorul are dreptul:</p>
        <ul>
          <li>De a audita respectarea prezentului acord.</li>
          <li>De a revoca accesul Procesatorului la date în orice moment.</li>
          <li>De a solicita ștergerea imediată a datelor în caz de încălcare a acordului.</li>
        </ul>

        <h3>8. Răspunderea</h3>
        <p>
          Procesatorul este responsabil pentru orice prejudiciu cauzat de prelucrarea neconformă
          a datelor, inclusiv pentru acțiunile sub-procesatorilor săi.
        </p>

        <h3>9. Legea aplicabilă</h3>
        <p>
          Prezentul acord este guvernat de legislația română și de Regulamentul (UE) 2016/679 (GDPR).
          Orice litigiu va fi soluționat de instanțele competente din Alba Iulia.
        </p>

        <p className="mt-6 text-sm text-muted-foreground">
          <strong>Versiunea DPA:</strong> 2.0 · <strong>Data:</strong> {new Date().toLocaleDateString('ro-RO')}
        </p>
      </div>

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="mt-6">
        <button
          onClick={handleAcceptDpa}
          disabled={accepting || missingFields.length > 0}
          className="w-full rounded-lg bg-[var(--vivid-purple)] py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90 disabled:opacity-50"
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          {accepting ? 'Se procesează...' : 'Accept Acordul de Prelucrare a Datelor'}
        </button>
      </div>
    </div>
  );
}
